import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { lightPalette } from "../../theme-layout/palette";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import { selectUserDetail } from "../../redux/slice/userSlice";
import { useSelector } from "react-redux";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import { useNavigate } from "react-router-dom";
import dataConstant from "../../constants/dataConstants";
import CryptoJS from "crypto-js";
import { encrypt } from "../../Helper/encryptHelper";

const defaultValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const ChangePasswordPage = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setError,
  } = useForm({ defaultValues });

  const [apiError, setApiError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const getUserDetail = useSelector(selectUserDetail);
  const newPassword = watch("newPassword");
  const [snack, closeSnack, showSnackbar] = SnackState();
  const navigate = useNavigate();
  // State to toggle password visibility
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const togglePassword = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const onSubmit = async (data) => {
    setApiError(null);
    setSuccessMessage("");

    try {
      const encryptedCurrentPassword = encrypt(data.currentPassword);
      const encryptedNewPassword = encrypt(data.newPassword);
      const response = await apiClient.post(urlConstant.Login.ChangePassword, {
        email: getUserDetail?.email,
        currentPassword: encryptedCurrentPassword,
        newPassword: encryptedNewPassword,
      });

      if (response?.statusCode === 200 && response?.status !== "Failure") {
        showSnackbar(response.message, "success");
        reset();
      } else {
        setApiError(response?.message || "Something went wrong");
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        Object.keys(serverErrors).forEach((key) => {
          setError(key, {
            type: "server",
            message: serverErrors[key][0],
          });
        });
      } else {
        setApiError(
          error.response?.data?.message || "Failed to update password"
        );
      }
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      sx={{
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: lightPalette.background.paper,
      }}
    >
      <Typography variant="h6" fontWeight="bold">
        Change Password
      </Typography>

      <Divider sx={{ my: 3 }} />

      {apiError && <Alert severity="error">{apiError}</Alert>}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}

      <Stack spacing={3} mt={2}>
        <Controller
          name="currentPassword"
          control={control}
          rules={{ required: "Current password is required" }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Current Password *"
              type={showPassword.current ? "text" : "password"}
              fullWidth
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePassword("current")}>
                      {showPassword.current ? (
                        <IconEyeOff stroke={1.5} />
                      ) : (
                        <IconEye stroke={1.5} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="newPassword"
          control={control}
          rules={{
            required: "New password is required",
            pattern: {
              value: dataConstant.passwordRegex,
              message:
                "Password must be 6-16 characters and include uppercase, lowercase, number, and special character",
            },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="New Password *"
              type={showPassword.new ? "text" : "password"}
              fullWidth
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePassword("new")}>
                      {showPassword.new ? (
                        <IconEyeOff stroke={1.5} />
                      ) : (
                        <IconEye stroke={1.5} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />

        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            required: "Confirm password is required",
            validate: (value) =>
              value === newPassword || "Confirm password do not match",
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Confirm Password *"
              type={showPassword.confirm ? "text" : "password"}
              fullWidth
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => togglePassword("confirm")}>
                      {showPassword.confirm ? (
                        <IconEyeOff stroke={1.5} />
                      ) : (
                        <IconEye stroke={1.5} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
        <Button variant="outlined" color="primary" onClick={() => {getUserDetail?.roles !== dataConstant.roles.marketer ? navigate('/dashboard') : navigate('/submission')}}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          sx={{ bgcolor: lightPalette.background.main }}
        >
          Save
        </Button>
      </Stack>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} duration={2000} />
    </Box>

  );
};

export default ChangePasswordPage;
